/**
 * UI 入力（`FamilyFormData`）からドメイン入力（`FamilyData`）への換算。
 *
 * UI は配偶者を「給与年収（万円）」で入力するが、ドメインの `FamilyData.spouseIncome` は
 * 「配偶者の合計所得（万円）」である。選択年分の給与所得控除を適用して合計所得へ換算する
 * ことで、所得要件（48/58/62万）との比較を正しく行う。
 *
 * 扶養親族は UI では「1人=1行（年齢・合計所得・同居老親等）」で入力し、ドメインの区分別人数
 * ・所得配列へ導出する。エンジン（`FamilyData`）の形は変更しない。
 *
 * 給与収入のみを前提とする（給与所得以外の所得は考慮しない）。
 */

import { of } from './taxRules';
import { createFamilyData, type DependentRow, type FamilyData, type FamilyFormData, type TaxRules } from './types';

/**
 * 配偶者の給与年収（万円）を合計所得（万円）へ換算する。
 * 合計所得 = 給与収入 − 給与所得控除（選択年分のルール）。
 */
export function spouseIncomeFromAnnualIncome(spouseAnnualIncomeMan: number, rules: TaxRules): number {
  const gross = Math.max(0, spouseAnnualIncomeMan) * 10000;
  const salaryDeduction = rules.salaryDeduction(gross);
  return (gross - salaryDeduction) / 10000;
}

/** 扶養親族の入力行から導出したドメイン入力（人数・所得配列）。 */
export interface DerivedDependents {
  dependents: number;
  dependents16To18: number;
  dependentIncomes: number[];
  dependentsUnder16: number;
  specificDependents: number;
  specificDependentIncomes: number[];
  elderlyDependents: number;
  elderlyDependentsLivingTogether: number;
}

/**
 * 扶養親族の入力行をドメインの区分別人数・所得配列へ導出する。
 *
 * - 16歳未満: 扶養控除の対象外（非課税判定の世帯人数にのみ算入）
 * - 16〜18歳・23〜69歳: 一般扶養親族（所得要件の判定に所得を用いる）
 * - 19〜22歳: 特定扶養親族（所得要件を超える場合は特定親族特別控除）
 * - 70歳以上: 老人扶養親族（同居老親等のチェックは70歳以上のみ有効）
 */
export function deriveDependents(rows: DependentRow[]): DerivedDependents {
  const general: DependentRow[] = [];
  const specific: DependentRow[] = [];
  let dependentsUnder16 = 0;
  let elderlyDependents = 0;
  let elderlyDependentsLivingTogether = 0;

  for (const row of rows) {
    const age = Math.max(0, Math.floor(row.age));
    if (age < 16) {
      dependentsUnder16 += 1;
    } else if (age < 19) {
      general.push(row);
    } else if (age < 23) {
      specific.push(row);
    } else if (age < 70) {
      general.push(row);
    } else {
      elderlyDependents += 1;
      if (row.livingTogether) {
        elderlyDependentsLivingTogether += 1;
      }
    }
  }

  return {
    dependents: general.length,
    dependents16To18: general.filter((row) => row.age >= 16 && row.age < 19).length,
    dependentIncomes: general.map((row) => row.incomeMan),
    dependentsUnder16,
    specificDependents: specific.length,
    specificDependentIncomes: specific.map((row) => row.incomeMan),
    elderlyDependents,
    elderlyDependentsLivingTogether,
  };
}

/**
 * UI 入力をドメイン入力へ変換する。配偶者がいない場合は合計所得 0 とする。
 * 本人の合計所得金額は常に給与収入から自動計算する（`housingTotalIncomeMan = 0`）。
 * 未対応年分の場合は `UnsupportedTaxYearError` を投げる。
 */
export function toFamilyData(form: FamilyFormData, taxYear: number): FamilyData {
  const rules = of(taxYear);
  return createFamilyData({
    ...deriveDependents(form.dependents),
    spouse: form.spouse,
    spouseIncome: form.spouse ? spouseIncomeFromAnnualIncome(form.spouseAnnualIncome, rules) : 0,
    spouseAge: form.spouse ? form.spouseAge : 0,
    workingStudent: form.workingStudent,
    lifeInsuranceGeneral: form.lifeInsuranceGeneral,
    lifeInsuranceMedical: form.lifeInsuranceMedical,
    lifeInsurancePension: form.lifeInsurancePension,
    housingMoveInYear: form.housingMoveInYear,
    housingMoveInMonth: form.housingMoveInMonth,
    housingAcquisition: form.housingAcquisition,
    housingCategory: form.housingCategory,
    housingChildRearing: form.housingChildRearing,
    housingSpecialAcquisition: form.housingSpecialAcquisition,
    housingYearEndBalance: form.housingYearEndBalance,
    housingCreditPossibleAmount: form.housingCreditPossibleAmount,
    housingTotalIncomeMan: 0,
    furusatoFilingMethod: form.furusatoFilingMethod,
  });
}
